/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress, Container, Box } from "@mui/material";
import {
  fetchSubmissionDetails,
  putSubmissionDetails,
  postSubmissionDetails,
  fetchUploadIconDetails,
  fetchSubmissionsForCreateNewRelease,
  fetchDeleteApplication,
} from "../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchApplicationDetails } from "../../Services/redux/appId-details/AppIdDeatilsActions";
import { fetchCategoryDetails } from "../../Services/redux/Category/CategoryActions";
import { fetchTeamDetails } from "../../Services/redux/team-details/TeamDetailsActions";
import "./ApplicationDetails.css";
import ListingHeader from "../../Components/ListingHeader/ListingHeader";
import Topheader from "../../Layout/header/header";
import ApplicationDetailsTab from "../../Components/ApplicationDetails-tab/ApplicationDetailsTab";
import AppDetailsInfo from "../../Components/AppDetails-info/AppDetailsInfo";
import AppDetailsLanguage from "../../Components/ApplicationDetails-Language/AppDetailsLanguage";
import { callNavigateButton } from "../../Services/redux/index";
import {
  fetchCurrentLanguage,
  fetchLanguageDetails,
} from "../../Services/redux/languages-details/LanguageDetailsActions";
import ApplicationDetailsAPKFile from "../../Components/APKFile/ApplicationDetailsAPKFile";
import ApplicationDetailsTargetting from "../../Components/ApplicationDetails-Targetting/ApplicationDetailsTargetting";
import ApplicationDetailsRollout from "../../Components/ApplicationDetailsRollout/ApplicationDetailsRollout";
import ApplicationDetailsIcon from "../../Components/ApplicationDetailsIcon/ApplicationDetailsIcon";
import ApplicationDetailsAgeRatingAds from "../../Components/ApplicationDetails-Age-Rating-Ads/ApplicationDetailsAgeRatingAds";
import ApplicationDetailsAdditionalDocument from "../../Components/ApplicationDetailsAdditionalDocument/ApplicationDetailsAdditionalDocument";
import ApplicationDetailsSideMenu from "../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationFileAPKUpload from "../../Components/ApplicationDetailsAPKFileUpload/ApplicationFileAPKUpload";
import ApplicationDetailsCategory from "../../Components/ApplicationDetailsCategory/ApplicationDetailsCategory";
import ApplicationDetailsErrorDialog from "../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import AppDetailsLicensing from "../../Components/ApplicationDetails-Licensing/AppDetailsLicensing";
import ApplicationDetailsUploadBinaries from "../../Components/ApplicationDetailsUploadBinaries/ApplicationDetailsUploadBinaries";
import ApplicationDetailsClusterCompatibity from "../../Components/ApplicationDetailsClusterCompatibity/ApplicationDetailsClusterCompatibity";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ApplicationDetailsLegalDocuments from "../../Components/ApplicationDetailsLegalDocuments/ApplicationDetailsLegalDocuments";
import ApplicationDetailsDLCM from "../../Components/ApplicationDetailsDLCM/ApplicationDetailsDLCM";
import ApplicationDetailsMonetize from "../../Components/ApplicationDetailsMonetize/ApplicationDetailsMonetize";
import AppDependency from "../../Components/AppDependency/AppDependency"
const COMPONENT_WIDTHS = {
  DRAFT: "1160px",
  NON_DRAFT: "1010px",
};

const PAYMENT_ROLES = {
  MONETIZE: "IGNITESTOREMONETIZE_SCP",
  MONETIZE_PREMIUM: "IGNITESTOREMONETIZEPREMIUM_SCP",
};

// Custom hook for user permissions
const useUserPermissions = () => {
  return useMemo(
    () => ({
      isIdpLogin: sessionStorage.getItem("idpLogin") === "true",
      isOemSeller: !!sessionStorage.getItem("isOemSeller"),
      paymentRole: sessionStorage.getItem("payment_role"),
      hasPaymentRole:
        sessionStorage
          .getItem("payment_role")
          ?.includes(PAYMENT_ROLES.MONETIZE) ||
        sessionStorage
          .getItem("payment_role")
          ?.includes(PAYMENT_ROLES.MONETIZE_PREMIUM),
    }),
    []
  );
};

// Custom hook for validation logic
const useSubmissionValidation = (
  submissionDetails,
  appData,
  userPermissions
) => {
  return useMemo(() => {
    if (!submissionDetails?.descriptions?.length) return false;

    // Description validation
    const validateDescriptions = () => {
      return submissionDetails.descriptions.every(
        (desc) =>
          (userPermissions.isIdpLogin ||
            userPermissions.isOemSeller ||
            desc.customerSupportLink) &&
          desc.longDescription &&
          desc.shortDescription &&
          desc.screenshots?.length > 0
      );
    };

    // Legal documents validation
    const validateLegalDocuments = () => {
      if (userPermissions.isIdpLogin) return true;

      const languages = submissionDetails.descriptions.map(
        (desc) => desc.language
      );
      return languages.every((lang) => {
        const privacyPolicy =
          submissionDetails.legalDocuments?.legalLinkInfos?.[lang]
            ?.legalLinks?.["Privacy Policy"];
        return privacyPolicy !== null;
      });
    };

    // Targeting validation
    const validateTargeting = () => {
      const { targeting } = submissionDetails;
      return (
        targeting.countriesInclusions?.length > 0 &&
        (userPermissions.isIdpLogin ||
          userPermissions.isOemSeller ||
          targeting.architectureTypesInclusions?.length > 0) &&
        targeting.vehiclesInclusions?.length > 0
      );
    };

    // Service/Product ID validation
    const validateServiceAndProductIds = () => {
      if (!userPermissions.isIdpLogin && !userPermissions.isOemSeller)
        return true;

      let valid = true;
      if (submissionDetails.enableServiceIds) {
        valid = valid && submissionDetails.associatedServiceIds?.length > 0;
      }
      if (submissionDetails.enableProductIds) {
        valid = valid && submissionDetails.associatedProductIds?.length > 0;
      }
      return valid;
    };

    const basicValidation =
      validateDescriptions() &&
      validateLegalDocuments() &&
      validateTargeting() &&
      validateServiceAndProductIds();

    // Additional validation for multi-submission apps
    if (
      appData?.submissions?.length > 1 &&
      appData.submissions[0]?.status === "DRAFT"
    ) {
      const binaryCheck = !!submissionDetails.binary && !!submissionDetails.bundle;
      const whatsNewCheck = submissionDetails.descriptions.every(
        (desc) => desc.whatIsNew && desc.whatIsNew.trim() !== ""
      );
      return basicValidation && binaryCheck && whatsNewCheck;
    }

    return basicValidation;
  }, [submissionDetails, appData, userPermissions]);
};

// Data loading hook
const useDataLoader = (appId, teamId, appMultiSubmissionId) => {
  const dispatch = useDispatch();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isCreateNewRelease, setIsCreateNewRelease]= useState(false)
  // Redux selectors
  const { loading, saving, submissionDetails, error } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const categoryData = useSelector((state) => state.categoryDetails);
  const teamsData = useSelector((state) => state.teamDetails.teams);
  const languageData = useSelector((state) => state.languageDetails.languages);
  const errors = useSelector((state) => state.errors);
  
  // Check if all required data is loaded
  const isDataReady = useMemo(() => {
    return (
      !loading &&
      submissionDetails &&
      Object.keys(submissionDetails).length > 0 &&
      submissionDetails.descriptions?.length > 0 &&
      appData &&
      Object.keys(appData).length > 0 &&
      categoryData.categories?.length > 0 &&
      languageData?.length > 0 &&
      teamsData &&
      Object.keys(teamsData).length > 0 &&
      appMultiSubmissionId !== undefined &&
      appMultiSubmissionId !== ""
    );
  }, [
    loading,
    submissionDetails,
    appData,
    categoryData,
    languageData,
    teamsData,
  ]);

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        dispatch(fetchCategoryDetails());
        dispatch(fetchTeamDetails(teamId));
        dispatch(fetchLanguageDetails("en-US"));
        dispatch(fetchApplicationDetails(appId));
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
    sessionStorage.setItem("teamId", teamId);

    return () => {
      dispatch(callNavigateButton());
    };
  }, [dispatch, appId, teamId]);

  // Load submission details when app data is ready
  useEffect(() => {
    if (!appData?.submissions?.length || isCreateNewRelease) return;
    const submissionIdToLoad = sessionStorage.getItem("submissionId");
    if (submissionIdToLoad) {
      dispatch(fetchSubmissionDetails(appId, submissionIdToLoad));
    }
  }, [dispatch, appId, appData, appMultiSubmissionId]);

  // Handle language loading when submission details are ready
  useEffect(() => {
    if (submissionDetails?.defaultLanguage) {
      const currentLanguage =
        sessionStorage.getItem("currentLanguage") ||
        submissionDetails.defaultLanguage;
      dispatch(fetchCurrentLanguage(currentLanguage));

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  }, [dispatch, submissionDetails, isInitialLoad]);

  return {
    isDataReady,
    loading,
    saving,
    submissionDetails,
    appData,
    categoryData,
    teamsData,
    languageData,
    errors,
    error,
    isCreateNewRelease,
    setIsCreateNewRelease

  };
};

function ApplicationDetailsNew(props) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state?.teamId;
  const [appMultiSubmissionId, setAppMultiSubmissionId] = useState(
    location.state?.submissionId
      ? location.state?.submissionId
      : sessionStorage.getItem("submissionId")
  );
  const newRelease = false;

  // Local state
  const [submissionId, setSubmissionId] = useState("");
  const [checkAll, setCheckAll] = useState(false);

  // Refs
  const APKfileRef = useRef(null);

  const dispatch = useDispatch();
  const userPermissions = useUserPermissions();

  // Load all data
  const {
    isDataReady,
    loading,
    saving,
    submissionDetails,
    appData,
    categoryData,
    teamsData,
    languageData,
    errors,
    isCreateNewRelease,
    setIsCreateNewRelease

  } = useDataLoader(appId, teamId, appMultiSubmissionId);

  const createNewRelase = isCreateNewRelease;

  // Validation
  const isValidSubmission = useSubmissionValidation(
    submissionDetails,
    appData,
    userPermissions
  );

  useEffect(() => {
    setCheckAll(isValidSubmission);
  }, [isValidSubmission]);

  // Set submission ID when data is ready
  useEffect(() => {
    if (isDataReady) {
      const targetSubmissionId =
        appMultiSubmissionId !== undefined ||
        location.state?.submissionId ||
        appData.submissions[0]?.id;
      if (submissionDetails && Object.keys(submissionDetails).length > 0) {
        setSubmissionId(submissionDetails.id);
        sessionStorage.setItem("submissionId", submissionDetails.id);
        setAppMultiSubmissionId(submissionDetails.id);
      } else if (targetSubmissionId) {
        setSubmissionId(targetSubmissionId);
        sessionStorage.setItem("submissionId", targetSubmissionId);
      }
    }
  }, [isDataReady, appMultiSubmissionId, appData, location.state]);

  // Memoized conditional checks
  const showComponents = useMemo(
    () => ({
      sideMenu: submissionDetails?.status !== "DRAFT" && isDataReady,
      tabs: submissionDetails?.status === "DRAFT" && isDataReady,
      uploadBinaries:
        (userPermissions.isOemSeller || userPermissions.isIdpLogin) &&
        (submissionDetails?.status === "APPROVED_FOR_STAGING" ||
          submissionDetails?.status === "APPROVED_FOR_BETA" ||
          submissionDetails?.status === "APPROVED_FOR_PRODUCTION") &&
        isDataReady,
      appDetailsInfo: submissionDetails?.status === "DRAFT",
      monetize: userPermissions.hasPaymentRole,
      clusterCompatibility: userPermissions.isIdpLogin,
      licensing: userPermissions.isOemSeller || userPermissions.isIdpLogin,
      legalDocuments: userPermissions.isIdpLogin,
      category: submissionDetails?.status !== "DRAFT",
    }),
    [submissionDetails, userPermissions, isDataReady]
  );

  // Memoized callbacks
  const postCallTheNewSubmission = useCallback(
    (data, checkingForRemoveScreenShots = false) => {
      if (!checkingForRemoveScreenShots) {
        dispatch(
          putSubmissionDetails(
            appId,
            sessionStorage.getItem("submissionId"),
            data
          )
        );
      }
    },
    [dispatch, appId, submissionId]
  );


const calPostApplicationNewRelase = useCallback(() => {
  dispatch(fetchSubmissionsForCreateNewRelease(appId));
  setIsCreateNewRelease(true);
}, [dispatch, appId, setIsCreateNewRelease]);

  const backToApplications = useCallback(() => {
    sessionStorage.removeItem("currentLanguage");
    navigate("/ui/developer/applications");
  }, [navigate]);

  const callNewSubmissionId = useCallback(
    (newSubmissionId) => {
      console.log("newSubmissionId======>",newSubmissionId);
      setSubmissionId(newSubmissionId);
      sessionStorage.setItem("submissionId", newSubmissionId);
      setAppMultiSubmissionId(newSubmissionId);
      dispatch(fetchSubmissionDetails(appId, newSubmissionId));
    },
    [dispatch, appId, submissionId]
  );

  const callPostSubmissionRequest = useCallback(
 (status, forceInstall = false) => {
    const submissionId = sessionStorage.getItem("submissionId");

    if (status === "delete") {
      return Promise.resolve(
        dispatch(fetchDeleteApplication(appId, submissionId))
      ).then(() => {
        navigate(-1);
      });
    }

    return Promise.resolve(
      dispatch(
        postSubmissionDetails(
          appId,
          submissionId,
          status,
          forceInstall
        )
      )
    );
  },
  [dispatch, appId, navigate]
);

  const callTheIconFileChange = useCallback(
    (file, id) => {
      dispatch(fetchUploadIconDetails(appId, id, file));
    },
    [dispatch, appId]
  );

  // Show loading spinner until all data is ready
  if (!isDataReady) {
    return (
      <>
        <Topheader />
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </>
    );
  }

  const isDraftStatus = submissionDetails.status === "DRAFT";

  return (
    <>
      <Topheader />
      <Container maxWidth="lg">
        <Box>
          <Box>
            <div
              className="container-application-details"
              style={{
                display: "flex",
                justifyContent: isDraftStatus ? "center" : "flex-start",
                flexDirection: isDraftStatus ? "column" : "row",
              }}
            >
              {/* Side Menu */}
              <div
                className="side-menu"
                style={{ marginTop: isDraftStatus ? "0px" : "69px" }}
              >
                <button className="arrow-back" onClick={backToApplications} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ArrowBackIcon style={{ color: "#717273" }} />
                  </div>
                  <div className="my-apps">My Apps</div>
                </button>

                {showComponents.sideMenu && (
                  <ApplicationDetailsSideMenu
                    appData={appData}
                    callNewSubmissionId={callNewSubmissionId}
                    teamId={teamId}
                    isMonetized={submissionDetails.isMonetized}
                    appMultiSubmissionId={appMultiSubmissionId}
                  />
                )}
              </div>

              {/* Main Content */}
              <div
                className="submission-container"
                style={{
                  width: "100%",
                  marginLeft: isDraftStatus ? "0" : "13%",
                }}
              >
                {/* Fixed Header */}
                <div
                  className="fixed-content-application-details"
                  style={{
                    width: isDraftStatus
                      ? COMPONENT_WIDTHS.DRAFT
                      : COMPONENT_WIDTHS.NON_DRAFT,
                  }}
                >
                  <ListingHeader
                    checkAll={checkAll}
                    callPostSubmissionRequest={callPostSubmissionRequest}
                    calPostApplicationNewRelase={calPostApplicationNewRelase}
                    postCallTheNewSubmission={postCallTheNewSubmission}
                     createNewRelase={createNewRelase}
                  />

                  {showComponents.tabs && (
                    <ApplicationDetailsTab checkAllTabsAreTrue={setCheckAll} />
                  )}
                </div>

                {/* Scrollable Content */}
                <div className="scrollable-content">
                  {showComponents.uploadBinaries && (
                    <ApplicationDetailsUploadBinaries />
                  )}

                  {showComponents.appDetailsInfo && (
                    <AppDetailsInfo
                      categoryData={categoryData.categories}
                      submissionData={submissionDetails}
                      languageData={languageData}
                      teamsData={teamsData}
                      postCallTheNewSubmission={postCallTheNewSubmission}
                    />
                  )}

                  <ApplicationDetailsAPKFile
                    ref={APKfileRef}
                    id="apkfile"
                    submissionData={submissionDetails}
                    createNewRelase={createNewRelase}
                  />

                  {(newRelease ||
                    (isDraftStatus &&
                      ((!submissionDetails?.binary &&
                        submissionDetails.applicationType === "APK") ||
                        (!submissionDetails?.bundle &&
                          submissionDetails.applicationType ===
                            "BUNDLE")))) && (
                    <ApplicationFileAPKUpload
                      checkAllTabsAreTrue={setCheckAll}
                    />
                  )}

                   {sessionStorage.getItem("domain") &&
                    sessionStorage.getItem("domain") !== "NONE" &&
                    sessionStorage.getItem("domain") !== "undefined" && (
                      <AppDependency
                        postCallTheNewSubmission={postCallTheNewSubmission}
                      />
                  )}
                  <AppDetailsLanguage
                    languageData={languageData}
                    submissionData={submissionDetails}
                    checkAllTabsAreTrue={setCheckAll}
                    postCallTheNewSubmission={postCallTheNewSubmission}
                  />
                  
                  {showComponents.monetize && (
                    <ApplicationDetailsMonetize
                      postCallTheNewSubmission={postCallTheNewSubmission}
                      teamId={teamId}
                    />
                  )}

                  <ApplicationDetailsIcon
                    callTheIconFileChange={callTheIconFileChange}
                    submissionData={submissionDetails}
                  />
                  <ApplicationDetailsAgeRatingAds 
                  postCallTheNewSubmission={postCallTheNewSubmission}/>
                  
                  {showComponents.clusterCompatibility && (
                    <ApplicationDetailsClusterCompatibity
                      postCallTheNewSubmission={postCallTheNewSubmission}
                    />
                  )}

                  {showComponents.clusterCompatibility && (
                    <ApplicationDetailsDLCM />
                  )}

                  <ApplicationDetailsAdditionalDocument
                    postCallTheNewSubmission={postCallTheNewSubmission}
                    submissionData={submissionDetails}
                    appId={appId}
                  />

                  {showComponents.licensing && (
                    <AppDetailsLicensing
                      postCallTheNewSubmission={postCallTheNewSubmission}
                      checkAllTabsAreTrue={setCheckAll}
                    />
                  )}

                  {showComponents.category && (
                    <ApplicationDetailsCategory
                      submissionData={submissionDetails}
                      categoryData={categoryData.categories}
                    />
                  )}

                  {showComponents.legalDocuments && (
                    <ApplicationDetailsLegalDocuments
                      postCallTheNewSubmission={postCallTheNewSubmission}
                    />
                  )}

                  <ApplicationDetailsTargetting
                    postCallTheNewSubmission={postCallTheNewSubmission}
                  />

                  <ApplicationDetailsRollout
                    postCallTheNewSubmission={postCallTheNewSubmission}
                  />
                </div>
              </div>
            </div>
          </Box>
        </Box>
      </Container>

      {/* Error Dialog */}
      {errors?.errorMessage && errors?.errorType && (
        <ApplicationDetailsErrorDialog />
      )}

      {saving && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            zIndex: 1000,
            border: "1px solid #e0e0e0",
          }}
        >
          <CircularProgress size={20} />
          <span style={{ fontSize: "14px", color: "#333" }}>
            Saving changes...
          </span>
        </div>
      )}

      {/* Loading Backdrop */}
      {loading && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </>
  );
}

export default ApplicationDetailsNew;
